import _ from 'lodash';
import { isProductionEnv } from '../../devOpsHelper/DevOps';
import { getCurrentStateKey } from '../../iwQueryClient/iwQueryClient';
import {
    buildOptionsFromDefault,
    deserialiseToEmpty,
    deserialiseToHTML,
    deserialiseToJSON,
    downloadAsAttachment
} from '../HelperAPI';
import { deserialisePostcodeLookupResponse } from './CustomerStateAPIHelper';

let stateKey = null;

export function setCurrentStateKey(state_key) {
    stateKey = state_key;
}

export function fetchSiteContext({ contextStateKey }) {
    let url = '/api/lending/edge/context/';

    if (contextStateKey) {
        url = url + `${contextStateKey}/`;
    }

    return fetchAsJSON(url);
}

export function fetchLendingState() {
    return fetchAsJSON(`/api/lending/edge/state/${getCurrentStateKey()}/`);
}

export function fetchSaveLendingState({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/edge/state/${impersonatingStateKey || stateKey}/`,
        {
            method: 'PUT',
            body
        }
    );
}

export function fetchAgreeSignDocument({ impersonatingStateKey, body }) {
    return fetchAsEmpty(
        `/api/lending/edge/loan_documents/${
            impersonatingStateKey || stateKey
        }/signed/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchFundingRequest({
    impersonatingStateKey,
    body,
    offerId,
    productType
}) {
    return fetchAsJSON(
        `/api/lending/edge/funding_request/${
            impersonatingStateKey || stateKey
        }/${productType}/${offerId}/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchCreateSetupIntent({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/v1/repayment/${
            impersonatingStateKey || stateKey
        }/payment_method/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchCreatePaymentIntent({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/edge/repayment/${
            impersonatingStateKey || stateKey
        }/payment_intent/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchSavePaymentMethod({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/v1/payment_method/${impersonatingStateKey || stateKey}/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchCreateTruelayerPayment({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/edge/early_repayment_truelayer/${
            impersonatingStateKey || stateKey
        }/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchSaveThreatmetrix({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/edge/threatmetrix/${
            impersonatingStateKey || stateKey
        }/event/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchUpdateThreatmetrix({
    impersonatingStateKey,
    body,
    sessionID
}) {
    return fetchAsEmpty(
        `/api/lending/edge/threatmetrix/${
            impersonatingStateKey || stateKey
        }/event/${sessionID}/`,
        {
            method: 'PUT',
            body
        }
    );
}

export function fetchUpdatePaymentMethod({
    impersonatingStateKey,
    paymentMethodID,
    body
}) {
    return fetchAsJSON(
        `/api/lending/v1/payment_method/${
            impersonatingStateKey || stateKey
        }/${paymentMethodID}/`,
        {
            method: 'PUT',
            body
        }
    );
}

export function fetchDeletePaymentMethod({
    impersonatingStateKey,
    paymentMethodID
}) {
    return fetchWithoutResponse(
        `/api/lending/v1/payment_method/${
            impersonatingStateKey || stateKey
        }/${paymentMethodID}/`,
        {
            method: 'DELETE'
        }
    );
}

export function fetchFundingRequirement({
    impersonatingStateKey,
    amount,
    offerId,
    productType
}) {
    return fetchAsJSON(
        `/api/lending/edge/funding_requirements/${
            impersonatingStateKey || stateKey
        }/${productType}/${offerId}/?cash_amount=${amount}`
    );
}

export function fetchProductOffers({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/product_offers/${impersonatingStateKey || stateKey}/`
    );
}

export function fetchFormalOffers({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/formal_offers/${impersonatingStateKey || stateKey}/`
    );
}

export function fetchFormaliseOffers({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/edge/formal_offers/${impersonatingStateKey || stateKey}/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchDeclineApplication({ impersonatingStateKey, body }) {
    return fetchAsEmpty(
        `/api/lending/edge/application/${
            impersonatingStateKey || stateKey
        }/decline/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchApplication({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/application/${impersonatingStateKey || stateKey}/`
    );
}

export function fetchSaveApplication({ impersonatingStateKey, body }) {
    return fetchAsEmpty(
        `/api/lending/edge/application/${impersonatingStateKey || stateKey}/`,
        {
            method: 'PUT',
            body
        }
    );
}

export function fetchAccounts({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/accounts/${impersonatingStateKey || stateKey}/`
    );
}

export function fetchEarlyRepaymentSimulation({
    impersonatingStateKey,
    accountId,
    amount
}) {
    return fetchAsJSON(
        `/api/lending/edge/early_repayment_simulation/${
            impersonatingStateKey || stateKey
        }/${accountId}/?amount=${amount}`
    );
}

export function fetchUploadedDocuments({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/document_upload/${
            impersonatingStateKey || stateKey
        }/`
    );
}

export function fetchSaveUploadedDocuments({ impersonatingStateKey, body }) {
    return fetchAsFormData(
        `/api/lending/edge/document_upload/${
            impersonatingStateKey || stateKey
        }/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchSaveUploadedDocumentsEdge({
    impersonatingStateKey,
    body
}) {
    return fetchAsFormData(
        `/api/lending/edge/document_upload/${
            impersonatingStateKey || stateKey
        }/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchSaveOnfidoSMS({ impersonatingStateKey, body }) {
    return fetchAsJSON(
        `/api/lending/edge/onfido/start/${impersonatingStateKey || stateKey}/`,
        {
            method: 'POST',
            body
        }
    );
}

export function fetchAllPayLinks({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/iwocapay/payment/later/${
            impersonatingStateKey || stateKey
        }/`
    );
}

export function fetchAllCapturedPayments({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/iwocapay/payment/captured/${
            impersonatingStateKey || stateKey
        }/`
    );
}

export function fetchOnlineAccounts({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/edge/online_account/${impersonatingStateKey || stateKey}/`
    );
}

export function fetchDeleteOnlineAccount({
    impersonatingStateKey,
    onlineAccountId
}) {
    return fetchAsEmpty(
        `/api/lending/edge/online_account/${
            impersonatingStateKey || stateKey
        }/${onlineAccountId}/`,
        {
            method: 'DELETE'
        }
    );
}

export function fetchAccountStatement({
    impersonatingStateKey,
    dateFrom,
    dateTo
}) {
    return fetchAsJSON(
        `/api/lending/edge/statements/${
            impersonatingStateKey || stateKey
        }/?date_from=${dateFrom}&date_to=${dateTo}`
    );
}

export function fetchSignDocumentByURL({ impersonatingStateKey, url, uid }) {
    //TODO: Potentially use directly the download url coming from the server
    // as during development it doesn't work
    if (!isProductionEnv()) {
        const localURL = `/api/lending/edge/loan_documents/${
            impersonatingStateKey || stateKey
        }/unsigned/${uid}/`;
        return fetchAsHTML(localURL);
    }

    return fetchAsHTML(url);
}

export function fetchSignedDocumentByURL({ impersonatingStateKey, url, uid }) {
    //TODO: Potentially use directly the download url coming from the server
    // as during development it doesn't work
    if (!isProductionEnv()) {
        const localURL = `/api/lending/edge/loan_documents/${
            impersonatingStateKey || stateKey
        }/signed/${uid}/`;
        return fetchAsHTML(localURL);
    }

    return fetchAsHTML(url);
}

export function downloadAccountStatement({
    impersonatingStateKey,
    dateFrom,
    dateTo,
    accountId
}) {
    return downloadAsPDF(
        `/api/lending/edge/statements/${
            impersonatingStateKey || stateKey
        }/?date_from=${dateFrom}&date_to=${dateTo}&account_id=${accountId}`
    );
}

export function downloadFile({ url }) {
    return downloadAsPDF(url);
}

export function fetchRequiredLoanDocuments({
    impersonatingStateKey,
    productType,
    formalOfferId,
    payLinkId,
    amount
}) {
    const urlParams = new URLSearchParams();
    if (payLinkId) urlParams.append('pay_link_id', payLinkId);
    if (amount) urlParams.append('cash_amount', amount);

    return fetchAsJSON(
        `/api/lending/edge/loan_documents/${
            impersonatingStateKey || stateKey
        }/required/${productType}/${formalOfferId}/?${urlParams.toString()}`
    );
}

export function fetchAddressLookup({ postcode }) {
    return fetchAsJSON(
        `/api/lending/edge/postcode_lookup/?postcode=${postcode}`
    ).then(deserialisePostcodeLookupResponse);
}

export function fetchCompanyLookup({ company }) {
    return fetchAsJSON(
        `/api/lending/edge/company_search/?company_name=${company}`
    );
}

export function fetchPaymentMethod({ impersonatingStateKey }) {
    return fetchAsJSON(
        `/api/lending/v1/payment_method/${impersonatingStateKey || stateKey}/`
    );
}

export function getOfferConfiguration({
    impersonatingStateKey,
    offerId,
    productType
}) {
    return fetchAsJSON(
        `/api/lending/edge/formal_offers/${
            impersonatingStateKey || stateKey
        }/${productType}/${offerId}/configuration/`
    );
}

export function postFundProductOfferRequest({
    impersonatingStateKey,
    offerId,
    productType,
    body
}) {
    return fetchAsEmpty(
        `/api/lending/edge/funding_request/${
            impersonatingStateKey || stateKey
        }/${productType}/${offerId}/`,
        { method: 'POST', body: JSON.stringify(body) }
    );
}

export function getRBLEstimatedSchedule({
    impersonatingStateKey,
    amount,
    sweepFraction,
    offerId
}) {
    const loanAmountQueryParam = `cash_amount=${amount}`;
    const sweepFractionQueryParam = sweepFraction
        ? `sweep_fraction=${sweepFraction}`
        : null;
    const queryParams = [loanAmountQueryParam, sweepFractionQueryParam]
        .filter(Boolean)
        .join('&');

    return fetchAsJSON(
        `/api/lending/edge/mca_estimated_schedule/${
            impersonatingStateKey || stateKey
        }/${offerId}/?${queryParams}`
    );
}

export function fetchDrawDownRepaymentSchedule({
    impersonatingStateKey,
    productType,
    offerId,
    payLinkId = null,
    amount = null,
    date = null
}) {
    if (payLinkId) {
        return fetchAsJSON(
            `/api/lending/edge/draw_down_repayment_schedule/${
                impersonatingStateKey || stateKey
            }/${productType}/${offerId}/?pay_link_id=${payLinkId}`
        );
    }
    if (date) {
        return fetchAsJSON(
            `/api/lending/edge/draw_down_repayment_schedule/${
                impersonatingStateKey || stateKey
            }/${productType}/${offerId}/?amount=${amount}&early_final_repayment_date=${date}`
        );
    }
    return fetchAsJSON(
        `/api/lending/edge/draw_down_repayment_schedule/${
            impersonatingStateKey || stateKey
        }/${productType}/${offerId}/?amount=${amount}`
    );
}

function fetchAsEmpty(url, overrideOptions) {
    const options = buildOptionsFromDefault(overrideOptions, {
        accept: 'application/json'
    });

    // eslint-disable-next-line pandora/prefer-lapi-client
    return fetch(url, options).then(deserialiseToEmpty);
}

function fetchAsJSON(url, overrideOptions) {
    const options = buildOptionsFromDefault(overrideOptions, {
        accept: 'application/json'
    });

    // eslint-disable-next-line pandora/prefer-lapi-client
    return fetch(url, options).then(deserialiseToJSON);
}

function fetchAsFormData(url, overrideOptions) {
    let options = buildOptionsFromDefault(overrideOptions, {
        enctype: 'multipart/form-data'
    });

    options = _.omit(options, 'headers.Content-Type');

    // eslint-disable-next-line pandora/prefer-lapi-client
    return fetch(url, options).then(deserialiseToJSON);
}

function fetchWithoutResponse(url, overrideOptions) {
    const options = buildOptionsFromDefault(overrideOptions);

    // eslint-disable-next-line pandora/prefer-lapi-client
    return fetch(url, options).then(() => ({}));
}

function downloadAsPDF(url, overrideOptions) {
    const options = buildOptionsFromDefault(overrideOptions, {
        accept: 'application/pdf'
    });

    // eslint-disable-next-line pandora/prefer-lapi-client
    return fetch(url, options).then(downloadAsAttachment);
}

function fetchAsHTML(url, overrideOptions) {
    const options = buildOptionsFromDefault(overrideOptions, {
        accept: 'text/html'
    });

    // eslint-disable-next-line pandora/prefer-lapi-client
    return fetch(url, options).then(deserialiseToHTML);
}
